/**
 * A ROLLING WINDOW OF TRAINING AS A GRID OF DAYS — the arithmetic, with no React in it.
 *
 * ═══ WHY THIS IS A CROSS-CUTTING MODULE AND NOT A COMPONENT IN A FEATURE ═══
 *
 * Two features draw this chart: history draws every session, and a movement screen draws that
 * one movement. Neither may import the other's components — the logs feature already renders
 * the movements feature's list, so a component borrowed the other way would close a cycle — and
 * duplicating a calendar grid is duplicating the one part of it that is easy to get subtly
 * wrong. `src/shared/` is the home the map gives a module that is a feature in every respect
 * except that it cuts across several.
 *
 * ═══ 🚨 NO DATE IS EVER PARSED FROM A STRING IN HERE ═══
 *
 * A day arrives as `YYYY-MM-DD` — a civil date, the day the athlete names — and it STAYS a
 * string. The grid is built by walking forward from a start date with `setDate`, formatting each
 * step back into the same shape, and looking values up in a map keyed by it.
 *
 * That is not a stylistic preference. `new Date("2026-08-25")` is parsed as UTC midnight, which
 * is the previous evening anywhere west of Greenwich, so every cell would sit one day out for
 * half the world — on a chart whose entire content is which day something happened.
 * `boiler/dates-only-in-converters` refuses the constructor outside a converter for exactly this
 * reason, and the walk below never needs it.
 */

/** One day in the grid. `value` is whatever the caller is measuring; zero means "did not train". */
export interface HeatmapDay {
  /** `YYYY-MM-DD`, the athlete's own calendar day. */
  day: string;
  value: number;
  /**
   * WHICH INTENSITY STEP TO PAINT, 0–4.
   *
   * Zero is "nothing happened" and is a different colour from "a little" rather than the palest
   * shade of it — a rest day and an easy day must not be told apart by squinting.
   */
  level: 0 | 1 | 2 | 3 | 4;
  /** False for a cell that pads the first or last column. Drawn as a hole, never as a rest day. */
  inRange: boolean;
}

/** One column. Seven days, Monday first. */
export interface HeatmapWeek {
  /** The Monday this column starts on, `YYYY-MM-DD`. */
  start: string;
  days: HeatmapDay[];
}

export interface HeatmapGrid {
  weeks: HeatmapWeek[];
  /**
   * WHERE EACH MONTH BEGINS, as a column index — the labels along the top.
   *
   * ⚠️ A MONTH IS LABELLED AT THE FIRST COLUMN THAT CONTAINS ITS FIRST WEEK, not at every column
   * inside it. Six labels over twenty-six columns is one per four or five, which is what makes
   * the axis readable; labelling each column would be twenty-six overlapping words.
   */
  months: { column: number; month: number }[];
  /** The largest value in the grid, so a caller can word its own legend. */
  peak: number;
}

/**
 * HOW MANY COLUMNS THE GRID IS — HALF A YEAR, NOT A YEAR.
 *
 * ═══ 🚨 IT WAS FIFTY-THREE, AND A YEAR DOES NOT FIT A PHONE ═══
 *
 * At a legible cell size a column is fourteen points wide, so a year is 742 of them against the
 * ~343 a 375pt phone actually has. Two thirds of the chart was off-screen, and the two thirds
 * you could see were the OLDEST — the athlete landed on last September and had to drag sideways
 * to reach the week they are in. A chart whose whole subject is "have I been training lately"
 * opened on the part of the year that answers that least.
 *
 * The alternatives were both worse. Shrinking the cells until a year fits makes each one ten
 * points, which is under a fingertip on a control you tap to read. Leaving it long and only
 * fixing the scroll position still hides five months behind a gesture nothing advertises.
 *
 * Twenty-six columns is half a year, it very nearly fits without scrolling, and it is still long
 * enough to show the thing this chart is for: a fortnight off reads as two blank columns, and a
 * block of consistent training reads as a solid run. What is lost is comparing this March to
 * last March, which no rolling window was ever the right tool for.
 *
 * ⚠️ IT REMAINS A ROLLING WINDOW ENDING AT TODAY, not a calendar half-year. It always contains
 * the week the athlete is in, which is the property that makes it worth looking at.
 */
export const HEATMAP_WEEKS = 26;

/** A second Date at the same instant, built without handing the constructor an argument. */
function sameInstant(source: Date): Date {
  const copy = new Date();
  copy.setTime(source.getTime());
  return copy;
}

/** `YYYY-MM-DD` from a Date's LOCAL fields — never `toISOString`, which is a day out west of UTC. */
function civilKey(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${String(date.getDate()).padStart(2, "0")}`;
}

/**
 * The Monday of the week `date` falls in, at local midnight. Sunday belongs to the week before.
 *
 * 🔑 THE SAME MONDAY EVERY OTHER WEEK BOUNDARY IN THIS PRODUCT USES — the sessions-per-week
 * arithmetic and the movement series both start there. Two different weeks in one app is a
 * discrepancy nobody can see and everybody feels.
 */
function startOfWeek(date: Date): Date {
  const monday = sameInstant(date);
  monday.setHours(0, 0, 0, 0);
  monday.setDate(monday.getDate() - (monday.getDay() === 0 ? 6 : monday.getDay() - 1));
  return monday;
}

/**
 * WHICH OF THE FOUR SHADES A VALUE GETS.
 *
 * ═══ 🚨 QUARTILES OF WHAT ACTUALLY HAPPENED, NOT FIXED THRESHOLDS ═══
 *
 * A fixed scale cannot serve two athletes. Someone doing three sets a day would be one flat pale
 * colour all year against a scale built for someone doing thirty, and the chart would say
 * "you never train" to a person who trains every day. Someone at the other end saturates at the
 * darkest shade in March and the rest of their year is a solid block.
 *
 * Splitting the athlete's OWN non-zero days into four means every shade is used, by
 * construction, and the chart always reads as a comparison of their days against each other —
 * which is the only comparison it was ever making.
 *
 * ⚠️ ZERO IS NOT A LEVEL. A day with no training is its own colour, not the palest step, because
 * "rested" and "trained lightly" are different facts and telling them apart must not require
 * comparing two nearly identical greys.
 */
export function levelFor(value: number, thresholds: readonly number[]): 0 | 1 | 2 | 3 | 4 {
  if (value <= 0) return 0;
  if (thresholds[2] !== undefined && value >= thresholds[2]) return 4;
  if (thresholds[1] !== undefined && value >= thresholds[1]) return 3;
  if (thresholds[0] !== undefined && value >= thresholds[0]) return 2;
  return 1;
}

/**
 * THE THREE CUTS THAT DIVIDE THE TRAINED DAYS INTO FOUR.
 *
 * Quartiles of the non-zero values. With fewer than four distinct days there are not four groups
 * to make, and the cuts collapse — which is correct: an athlete with two sessions of history
 * gets two shades, not four invented ones.
 */
export function thresholdsFor(values: readonly number[]): number[] {
  const trained = values.filter((value) => value > 0).sort((a, b) => a - b);
  if (trained.length === 0) return [];

  const at = (share: number) => trained[Math.min(trained.length - 1, Math.floor(trained.length * share))]!;
  return [at(0.25), at(0.5), at(0.75)];
}

/**
 * THE GRID — `weeks` columns ending with the week `today` falls in.
 *
 * `values` is keyed by `YYYY-MM-DD`; a day absent from it is a day with no training, which is a
 * real answer and the most common one.
 *
 * ⚠️ IT ENDS ON THE CURRENT WEEK RATHER THAN ON TODAY, so the last column is a full seven cells
 * and the grid does not change shape as the week goes on. Days after today are marked out of
 * range — the future is drawn as a hole, never as a rest day the athlete has already failed to
 * fill.
 */
export function buildGrid(
  values: ReadonlyMap<string, number>,
  weeks: number = HEATMAP_WEEKS,
  today: Date = new Date(),
): HeatmapGrid {
  const thresholds = thresholdsFor([...values.values()]);
  const todayKey = civilKey(today);

  const firstMonday = startOfWeek(today);
  firstMonday.setDate(firstMonday.getDate() - (weeks - 1) * 7);

  const columns: HeatmapWeek[] = [];
  const months: { column: number; month: number }[] = [];
  let peak = 0;
  let lastMonth = -1;

  const cursor = sameInstant(firstMonday);

  for (let column = 0; column < weeks; column += 1) {
    const days: HeatmapDay[] = [];
    const start = civilKey(cursor);

    for (let weekday = 0; weekday < 7; weekday += 1) {
      const day = civilKey(cursor);
      const value = values.get(day) ?? 0;
      if (value > peak) peak = value;

      /* THE FUTURE IS A HOLE. Comparing the civil strings is a valid ordering because they are
         fixed-width and zero-padded, which is the whole reason this format is used. */
      const inRange = day <= todayKey;
      days.push({ day, value, level: inRange ? levelFor(value, thresholds) : 0, inRange });

      /* The month label goes on the column whose MONDAY starts a new month — read from the first
         day of the column, so a month never gets two labels because its 1st fell mid-week. */
      if (weekday === 0) {
        const month = cursor.getMonth();
        if (month !== lastMonth) {
          months.push({ column, month });
          lastMonth = month;
        }
      }

      cursor.setDate(cursor.getDate() + 1);
    }

    columns.push({ start, days });
  }

  return { weeks: columns, months, peak };
}
